import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, MessageCircle } from "lucide-react";
import axios from "axios";
import { SERVICES } from "@/constants/aero";
import { SOCIALS } from "@/constants/content";
import { EquipmentShowcase } from "./EquipmentShowcase";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];

export const AeroServices = () => {
  const [waHref, setWaHref] = useState(SOCIALS.instagram);

  useEffect(() => {
    axios
      .get(`${API}/config`)
      .then((r) => {
        if (r.data.whatsapp_number) {
          setWaHref(`https://wa.me/${r.data.whatsapp_number}?text=${encodeURIComponent("Hi Akki! Shoot book karna hai — details baat karni hain.")}`);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <section id="services" data-testid="aero-services" className="bg-white py-[100px] md:py-[120px]">
      <div className="max-w-[1320px] mx-auto px-6 md:px-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease }}
          className="max-w-2xl"
        >
          <p className="font-mono text-[12px] tracking-[0.35em] uppercase text-[#4F6D8A]">Production Studio</p>
          <h2 className="mt-4 font-serif text-[34px] md:text-[48px] xl:text-[56px] leading-[1.05] text-[#111827]">
            Book a Cinematic Experience
          </h2>
          <motion.span
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.25, ease }}
            className="mt-5 block w-14 h-[2px] bg-[#4F6D8A] origin-left"
          />
          <p className="mt-5 text-[#6B7280] leading-relaxed">
            Every project is approached like a film — from pre-production planning to the final cinematic delivery.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="mt-14 grid md:grid-cols-2 xl:grid-cols-3 gap-7">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.08, ease }}
              data-testid={`aero-service-card-${i}`}
              className="group bg-white rounded-[22px] border border-[#E5E7EB] overflow-hidden shadow-[0_10px_35px_-15px_rgba(15,42,67,0.15)] hover:-translate-y-1.5 hover:shadow-[0_35px_70px_-20px_rgba(15,42,67,0.35)] transition-all duration-300"
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={s.img}
                  alt={s.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <span className="absolute inset-0 bg-[#0F2A43]/0 group-hover:bg-[#0F2A43]/35 transition-colors duration-300" />
                <span className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-[#0F2A43] opacity-0 -rotate-45 group-hover:opacity-100 group-hover:rotate-0 transition-all duration-300">
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </span>
              </div>
              <div className="p-7">
                <p className="font-display font-bold text-xl text-[#111827]">{s.title}</p>
                <p className="mt-1.5 font-mono text-[9px] tracking-[0.22em] uppercase text-[#4F6D8A]">{s.sub}</p>
                <ul className="mt-5 space-y-2.5">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-center gap-2.5 text-sm text-[#6B7280]">
                      <Check size={13} strokeWidth={2} className="text-[#4F6D8A] shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  data-testid={`aero-service-book-${i}`}
                  href="/book-shoot"
                  className="mt-7 inline-flex w-full items-center justify-center gap-2 bg-[#0F2A43] text-white font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-6 py-4 rounded-full shadow-[0_12px_30px_-10px_rgba(15,42,67,0.45)] hover:bg-[#16395C] hover:-translate-y-0.5 transition-all duration-300"
                >
                  Book This Service <ArrowRight size={13} />
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Equipment We Use — between service cards and booking CTA */}
        <EquipmentShowcase />

        {/* CTA Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease }}
          data-testid="aero-services-banner"
          className="mt-16 bg-[#0F2A43] rounded-[28px] px-8 py-12 md:p-16 text-center shadow-[0_40px_90px_-25px_rgba(15,42,67,0.55)]"
        >
          <p className="font-mono text-[12px] tracking-[0.35em] uppercase text-[#8FAECC]">Let's Create Together</p>
          <h3 className="mt-4 font-serif text-3xl md:text-5xl text-white leading-tight">
            Let's Create Your Next Cinematic Story
          </h3>
          <p className="mt-5 text-white/65 max-w-xl mx-auto leading-relaxed">
            Whether it's a feature film, brand campaign, wedding, or aerial production — let's turn your vision into a cinematic experience.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <a
              data-testid="aero-services-banner-book"
              href="/book-shoot"
              className="inline-flex items-center gap-2 bg-white text-[#0F2A43] font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:bg-[#8FAECC] hover:text-white transition-all duration-300 hover:-translate-y-0.5"
            >
              Book a Shoot <ArrowUpRight size={13} />
            </a>
            <a
              data-testid="aero-services-banner-whatsapp"
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-white/35 text-white font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:border-white hover:bg-white/10 transition-all duration-300"
            >
              <MessageCircle size={13} /> WhatsApp Me
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
